import { Link } from 'react-router-dom';
import { Syringe, Stethoscope, Pill, Scissors, BookOpen, DollarSign, ClipboardList, FileDown, Shield, Check, Camera, MessageCircle, Sparkles, CalendarDays, Phone, ShoppingBag, Gift, Award } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd7e4899-b441-40b2-8a4d-ff26ed1613bb/artifacts/tc6miqnj_1000017045.jpg";
const HERO_IMG = "https://images.pexels.com/photos/2401877/pexels-photo-2401877.jpeg?auto=compress&cs=tinysrgb&w=1200";

const features = [
  { icon: Syringe, title: "Vaccination Records", desc: "Track vaccines, due dates, and providers.", bg: "#F9F7F3" },
  { icon: Stethoscope, title: "Veterinary Visits", desc: "Log vet visits, diagnoses, and follow-ups.", bg: "#FFFFFF" },
  { icon: Pill, title: "Medication Tracker", desc: "Monitor dosages, schedules, and notes.", bg: "#E9EFE8" },
  { icon: Scissors, title: "Farrier Visit Log", desc: "Record trims, shoeing, and hoof health.", bg: "#F9F7F3" },
  { icon: BookOpen, title: "Training Journal", desc: "Document sessions, goals, and progress.", bg: "#FFFFFF" },
  { icon: DollarSign, title: "Expense Tracker", desc: "Manage all equine-related expenses.", bg: "#E9EFE8" },
  { icon: ClipboardList, title: "Barn Chore Planner", desc: "Organize daily and recurring barn tasks.", bg: "#F9F7F3" },
  { icon: FileDown, title: "PDF Export", desc: "Download or print professional records.", bg: "#FFFFFF" },
];

const subscriberFeatures = [
  { icon: Check, text: "Unlimited horses" },
  { icon: Syringe, text: "All 9 health & activity record trackers" },
  { icon: FileDown, text: "PDF export & download for all records" },
  { icon: Camera, text: "Secure cloud photo storage & gallery" },
  { icon: Sparkles, text: "AI-powered health insights & recommendations" },
  { icon: CalendarDays, text: "Appointment calendar with auto-reminders" },
  { icon: MessageCircle, text: "Subscriber community chat (1-on-1 & group)" },
  { icon: ShoppingBag, text: "Horse marketplace (buy, sell, or lease)" },
  { icon: Phone, text: "Emergency contacts quick-access directory" },
  { icon: Gift, text: "Referral program — earn $5 per friend" },
  { icon: Award, text: "Registration & pedigree tracking (AQHA, APHA, etc.)" },
  { icon: BookOpen, text: "Breeding log & show records" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      {/* Hero Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-[#F2F7F4]/90 border-b border-[#D1E0D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Stable Solutions" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <span className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] block leading-tight">Stable Solutions</span>
              <span className="text-[10px] font-medium tracking-wider text-[#4B6B58] uppercase">The Oaks Legacy Ranch</span>
            </div>
          </div>
          <Link
            to="/auth"
            data-testid="hero-get-started-btn"
            className="px-6 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
          >
            Start Free Month
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-in">
            <p className="text-sm font-semibold tracking-widest text-[#2D6A4F] uppercase mb-4">Equine Care Planners & Barn Organization Tools</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B4332] tracking-tight leading-[1.1] mb-6">
              Everything Your Barn Needs, Organized
            </h1>
            <p className="text-base sm:text-lg text-[#4B6B58] leading-relaxed mb-10 max-w-lg">
              From vaccination records to farrier logs, manage every aspect of your horses' health and your barn operations in one beautiful, easy-to-use platform.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/auth"
                data-testid="hero-start-trial-btn"
                className="px-8 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_8px_30px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
              >
                Start Your Free Month
              </Link>
              <a
                href="#features"
                data-testid="hero-learn-more-btn"
                className="px-8 py-3.5 rounded-full border-2 border-[#1B4332] text-[#1B4332] font-semibold btn-lift hover:bg-[#1B4332] hover:text-white"
              >
                Learn More
              </a>
            </div>
            <p className="text-xs text-[#4B6B58] mt-3">First month completely free. Then just $9.95/mo for your first year.</p>
          </div>
          <div className="animate-fade-in-delay-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#1B4332]/5 rounded-[2.5rem] -z-10"></div>
              <img
                src={HERO_IMG}
                alt="Horses in pasture"
                className="rounded-[2rem] w-full h-[320px] sm:h-[420px] object-cover shadow-[0_8px_30px_rgb(27,67,50,0.12)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-14">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-3">Complete Equine Management</h2>
          <p className="text-base text-[#4B6B58] max-w-xl mx-auto">All the tools you need to keep your horses healthy and your barn running smoothly.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className={`p-6 rounded-[2rem] card-hover animate-fade-in-delay-${Math.min(i % 4 + 1, 4)} shadow-[0_8px_30px_rgb(27,67,50,0.06)]`}
              style={{ backgroundColor: f.bg }}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#1B4332]/10 flex items-center justify-center mb-5">
                <f.icon size={22} className="text-[#1B4332]" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">{f.title}</h3>
              <p className="text-sm text-[#4B6B58] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Everything You Get */}
      <section id="subscriber-features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 shadow-[0_8px_30px_rgb(27,67,50,0.08)]" data-testid="subscriber-features-section">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-bold uppercase tracking-wider mb-4">Full Access</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-3">Everything Your Subscription Includes</h2>
            <p className="text-base text-[#4B6B58] max-w-lg mx-auto">One simple plan. Every feature. No hidden extras.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {subscriberFeatures.map((f, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F2F7F4] transition-colors" data-testid={`subscriber-feature-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-[#E9EFE8] flex items-center justify-center flex-shrink-0">
                  <f.icon size={16} className="text-[#2D6A4F]" />
                </div>
                <span className="text-sm text-[#122A1F] font-medium">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="text-center mb-14">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-3">Simple, Honest Pricing</h2>
          <p className="text-base text-[#4B6B58]">Try everything free for 30 days. No credit card needed.</p>
        </div>
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-[2rem] p-10 shadow-[0_8px_30px_rgb(27,67,50,0.08)] relative overflow-hidden" data-testid="pricing-card">
            {/* Free badge */}
            <div className="absolute top-5 right-5">
              <span className="px-4 py-1.5 rounded-full bg-[#2D6A4F] text-white text-xs font-bold uppercase tracking-wider shadow-[0_4px_12px_rgb(27,67,50,0.2)]">
                1st Month Free
              </span>
            </div>
            <div className="w-16 h-16 rounded-full bg-[#1B4332]/10 flex items-center justify-center mx-auto mb-6">
              <Shield size={28} className="text-[#1B4332]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-6 text-center">Monthly Plan</h3>

            {/* Pricing tiers */}
            <div className="space-y-4 mb-8">
              <div className="bg-[#E9EFE8] rounded-[1.5rem] p-5 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#2D6A4F] mb-1">First Month</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#2D6A4F]">$0</span>
                </div>
                <p className="text-xs text-[#4B6B58] mt-1">Free trial — full access, no card required</p>
              </div>
              <div className="bg-[#F9F7F3] rounded-[1.5rem] p-5 text-center relative">
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#1B4332] text-white text-[10px] font-bold uppercase tracking-wider">Best Value</span>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#8B6914] mb-1 mt-1">Months 2–12 (Subscribe During Trial)</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#1B4332]">$9.95</span>
                  <span className="text-[#4B6B58] text-sm">/month</span>
                </div>
                <p className="text-xs text-[#4B6B58] mt-1">Lock in this rate for your entire first year</p>
              </div>
              <div className="bg-white border border-[#D1E0D5] rounded-[1.5rem] p-5 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#4B6B58] mb-1">After 1st Year / Late Signup</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#4B6B58]">$14.95</span>
                  <span className="text-[#4B6B58] text-sm">/month</span>
                </div>
                <p className="text-xs text-[#4B6B58] mt-1">Regular monthly rate</p>
              </div>
            </div>

            <Link
              to="/auth"
              data-testid="pricing-get-started-btn"
              className="block w-full py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] text-center"
            >
              Start Free Month
            </Link>
            <p className="text-xs text-[#4B6B58] mt-3 text-center">Cancel anytime. No strings attached.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#D1E0D5] mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Stable Solutions" className="h-8 w-8 rounded-full object-cover" />
              <div>
                <span className="font-['Cormorant_Garamond'] text-sm font-semibold text-[#1B4332] block">Stable Solutions by The Oaks Legacy Ranch</span>
                <span className="text-[10px] text-[#4B6B58] leading-snug block max-w-sm">The Oaks Legacy Ranch is a program under our 501(c)(3) Non-Profit The N.I.C.E. Foundation. 100% of proceeds support our community agendas.</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-xs text-[#4B6B58] hover:text-[#2D6A4F] hover:underline" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/privacy" className="text-xs text-[#4B6B58] hover:text-[#2D6A4F] hover:underline" data-testid="footer-privacy-link">Privacy Policy</Link>
            </div>
          </div>
          <p className="text-xs text-[#4B6B58] text-center">&copy; {new Date().getFullYear()} The New Initiative Community Enhancement Foundation (The N.I.C.E. Foundation). All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
